import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  ShieldCheck, 
  Truck, 
  CreditCard, 
  PhoneCall, 
  Copy, 
  Check, 
  Building2, 
  Sparkles, 
  MessageCircle,
  ArrowRight,
  UserCheck
} from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { PAKISTAN_CITIES } from "@/constants/pakistanCities";
import { POSTEX_SHIPPING_CITIES } from "@/constants/postexShippingCities";
import apiClient, { resolveImageUrl } from "@/utils/api";

export default function Checkout() {
  const { cartItems, subtotal, clearCart } = useCart();
  const { settings } = useSiteSettings();
  const { customer, isSignedIn, signInWithGoogle } = useCustomerAuth();
  const navigate = useNavigate();

  // Form states
  const [customerName, setCustomerName] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [sameAsMobile, setSameAsMobile] = useState(true);
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [city, setCity] = useState("");
  const [cityQuery, setCityQuery] = useState("");
  const [cityOpen, setCityOpen] = useState(false);
  const [orderNotes, setOrderNotes] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cod"); // "cod", "bank_transfer", "easypaisa", "jazzcash", "whatsapp_direct"
  const [copiedField, setCopiedField] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Auto-fill saved details when a signed-in customer arrives at checkout
  useEffect(() => {
    if (!customer) return;
    setCustomerName((v) => v || customer.name || "");
    setMobileNumber((v) => v || customer.saved_mobile || "");
    setWhatsappNumber((v) => v || customer.saved_whatsapp || customer.saved_mobile || "");
    setDeliveryAddress((v) => v || customer.saved_address || "");
    if (customer.saved_city) setCity((v) => v || customer.saved_city);
  }, [customer]);

  // Weight-bracket delivery + COD 4% preview (server is authoritative — clients cannot override).
  const totalWeightKg = cartItems.reduce(
    (sum, it) => sum + (Number(it.weight_kg) || 0.5) * it.quantity,
    0
  );
  const calcDelivery = (kg) => {
    if (kg <= 0) return 0;
    if (kg <= 1) return 350;
    if (kg <= 2) return 500;
    if (kg <= 3) return 600;
    if (kg <= 5) return 800;
    if (kg <= 10) return 1000;
    return 1000 * Math.ceil(kg / 10);
  };
  const shippingFee = calcDelivery(totalWeightKg);
  const codCharge = paymentMethod === "cod" ? Math.round((subtotal + shippingFee) * 0.04) : 0;
  const grandTotal = subtotal + shippingFee + codCharge;

  const copyToClipboard = (text, fieldName) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    toast.success(`Copied ${fieldName} to clipboard!`);
    setTimeout(() => setCopiedField(null), 2500);
  };

  const handleMobileChange = (val) => {
    setMobileNumber(val);
    if (sameAsMobile) {
      setWhatsappNumber(val);
    }
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    if (cartItems.length === 0) {
      toast.error("Your cart is empty. Please add items to order.");
      return;
    }

    if (!customerName.trim() || !mobileNumber.trim() || !deliveryAddress.trim() || !city.trim()) {
      toast.error("Please fill in all mandatory fields (Name, Phone, Address, City).");
      return;
    }

    // Enforce PostEx-listed city (source of truth = PostEx_Cities_Cleaned.csv).
    if (!POSTEX_SHIPPING_CITIES.includes(city.trim())) {
      toast.error("Please pick your city from the PostEx delivery list.");
      return;
    }

    setSubmitting(true);
    try {
      const orderPayload = {
        customer_name: customerName.trim(),
        mobile_number: mobileNumber.trim(),
        whatsapp_number: (sameAsMobile ? mobileNumber : whatsappNumber).trim(),
        email: customer?.email || null,
        delivery_address: deliveryAddress.trim(),
        city: city.trim(),
        payment_method: paymentMethod,
        order_notes: orderNotes.trim() || null,
        items: cartItems.map((item) => ({
          product_id: item.product_id,
          product_name: item.product_name,
          category: item.category,
          price: item.price,
          quantity: item.quantity,
          selected_color: item.selected_color,
          selected_design: item.selected_design,
          is_complete_set: item.is_complete_set,
          set_details: item.set_details,
          image: item.image,
          item_total: item.item_total,
        })),
        subtotal: subtotal,
        shipping_fee: shippingFee,
        total_amount: grandTotal,
        order_type: "retail",
      };

      const { data } = await apiClient.post("/orders", orderPayload);
      if (data && data.success) {
        clearCart();
        toast.success("Order Placed Successfully!");
        navigate(`/order-confirmation/${data.order.order_number}`, { state: { order: data.order } });
      } else {
        toast.error("Could not place order. Please try again.");
      }
    } catch (err) {
      console.error("Order submission error", err);
      toast.error("Order placement failed. Please check your network or WhatsApp us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center space-y-4">
        <h2 className="font-heading text-2xl font-bold">No Items in Cart to Checkout</h2>
        <Link to="/shop" className="px-5 py-2.5 bg-[#1C1917] text-white text-xs font-bold rounded-lg inline-block">
          Explore Catalog
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      
      {/* Title */}
      <div className="border-b border-[#E5E0D8] pb-4">
        <h1 data-testid="checkout-page-title" className="font-heading text-3xl font-bold text-stone-900">
          Order Checkout & Delivery Details
        </h1>
        <p className="text-xs sm:text-sm text-stone-500 mt-1">
          Complete your order with Cash on Delivery (COD), Bank Transfer, EasyPaisa, or JazzCash across Pakistan.
        </p>
      </div>

      <form onSubmit={handleSubmitOrder} className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        
        {/* Left Form: Delivery & Payment Methods (7 cols) */}
        <div className="lg:col-span-7 space-y-8">

          {/* Sign-in prompt for guests (autoprefill for signed-in) */}
          {!isSignedIn ? (
            <div
              data-testid="checkout-signin-prompt"
              className="bg-[#FFFBF0] border border-[#D4AF37]/50 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between"
            >
              <div className="space-y-0.5">
                <p className="text-xs font-bold text-stone-900">Sign in to check out in one tap next time</p>
                <p className="text-[11px] text-stone-500">Your name, mobile and address get saved securely for next order.</p>
              </div>
              <button
                type="button"
                onClick={signInWithGoogle}
                data-testid="checkout-signin-btn"
                className="whitespace-nowrap px-4 py-2 bg-white border border-stone-300 hover:border-[#D4AF37] rounded-lg font-semibold text-xs text-stone-800 flex items-center gap-2"
              >
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-4 h-4" />
                <span>Continue with Google</span>
              </button>
            </div>
          ) : (
            <div
              data-testid="checkout-signedin-banner"
              className="bg-emerald-50 border border-emerald-200 rounded-2xl p-3 flex items-center gap-2 text-xs text-emerald-900"
            >
              <UserCheck className="w-4 h-4" />
              <span>
                Signed in as <strong>{customer?.name || customer?.email}</strong> — your saved delivery details have been auto-filled below.
              </span>
            </div>
          )}
          
          {/* Customer Details Box */}
          <div className="bg-white p-6 rounded-2xl border border-[#E5E0D8] shadow-sm space-y-4">
            <h2 className="font-heading font-bold text-base text-stone-900 flex items-center gap-2">
              <Truck className="w-4 h-4 text-[#D4AF37]" />
              1. Customer & Shipping Address
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
              
              {/* Name */}
              <div className="sm:col-span-2 space-y-1">
                <label className="font-semibold text-stone-800">
                  Full Customer Name <span className="text-rose-600">*</span>
                </label>
                <input
                  type="text"
                  required
                  data-testid="checkout-name-input"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="e.g. Mrs. Ayesha Tariq / Muhammad Ali"
                  className="w-full px-3.5 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                />
              </div>

              {/* Mobile Phone */}
              <div className="space-y-1">
                <label className="font-semibold text-stone-800">
                  Mobile Number (For Courier) <span className="text-rose-600">*</span>
                </label>
                <input
                  type="tel"
                  required
                  data-testid="checkout-mobile-input"
                  value={mobileNumber}
                  onChange={(e) => handleMobileChange(e.target.value)}
                  placeholder="0300 1234567"
                  className="w-full px-3.5 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                />
              </div>

              {/* WhatsApp Number */}
              <div className="space-y-1">
                <label className="font-semibold text-stone-800 flex items-center justify-between">
                  <span>WhatsApp Number</span>
                  <label className="font-normal text-[10px] text-stone-500 cursor-pointer flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={sameAsMobile}
                      onChange={(e) => {
                        setSameAsMobile(e.target.checked);
                        if (e.target.checked) setWhatsappNumber(mobileNumber);
                      }}
                      className="rounded text-[#D4AF37]"
                    />
                    Same as mobile
                  </label>
                </label>
                <input
                  type="tel"
                  data-testid="checkout-whatsapp-input"
                  value={sameAsMobile ? mobileNumber : whatsappNumber}
                  disabled={sameAsMobile}
                  onChange={(e) => setWhatsappNumber(e.target.value)}
                  placeholder="0300 1234567"
                  className="w-full px-3.5 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg focus:outline-none focus:border-[#D4AF37] disabled:opacity-75"
                />
              </div>

              {/* City Selection — searchable PostEx city combobox */}
              <div className="space-y-1 relative">
                <label className="font-semibold text-stone-800">
                  City in Pakistan <span className="text-rose-600">*</span>
                </label>
                {(() => {
                  const q = cityQuery.trim().toLowerCase();
                  const matches = q
                    ? POSTEX_SHIPPING_CITIES.filter((c) => c.toLowerCase().includes(q)).slice(0, 60)
                    : POSTEX_SHIPPING_CITIES.slice(0, 60);
                  const validPick = city && POSTEX_SHIPPING_CITIES.includes(city);
                  return (
                    <>
                      <input
                        type="text"
                        data-testid="checkout-city-input"
                        value={cityQuery !== "" || cityOpen ? cityQuery : city}
                        onChange={(e) => {
                          setCityQuery(e.target.value);
                          setCityOpen(true);
                          if (!e.target.value) setCity("");
                        }}
                        onFocus={() => {
                          setCityOpen(true);
                          setCityQuery(city || "");
                        }}
                        onBlur={() => {
                          // Delay so a click on the list can register first
                          setTimeout(() => setCityOpen(false), 150);
                        }}
                        placeholder="Type your city (e.g. LAHORE, KARACHI SADDAR)..."
                        aria-autocomplete="list"
                        aria-expanded={cityOpen}
                        className="w-full px-3.5 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                      />
                      {cityOpen && (
                        <ul
                          data-testid="checkout-city-suggestions"
                          className="absolute z-20 left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-white border border-stone-300 rounded-lg shadow-luxury"
                        >
                          {matches.length === 0 ? (
                            <li className="px-3 py-2 text-[11px] text-stone-500 italic">
                              No PostEx city matches "{cityQuery}". Try a different spelling.
                            </li>
                          ) : (
                            matches.map((c) => (
                              <li key={c}>
                                <button
                                  type="button"
                                  data-testid={`checkout-city-option-${c.replace(/\s+/g, "-")}`}
                                  onMouseDown={(ev) => ev.preventDefault()}
                                  onClick={() => {
                                    setCity(c);
                                    setCityQuery("");
                                    setCityOpen(false);
                                  }}
                                  className={`w-full text-left px-3 py-1.5 text-xs hover:bg-[#FAF8F5] ${
                                    city === c ? "bg-[#D4AF37]/15 font-bold text-[#996515]" : "text-stone-800"
                                  }`}
                                >
                                  {c}
                                </button>
                              </li>
                            ))
                          )}
                          {matches.length >= 60 && (
                            <li className="px-3 py-1.5 text-[10px] text-stone-500 italic border-t border-stone-100">
                              Showing first 60. Refine your search to see more.
                            </li>
                          )}
                        </ul>
                      )}
                      <div className="flex items-center justify-between text-[10px] mt-0.5">
                        <span
                          data-testid="checkout-city-status"
                          className={validPick ? "text-emerald-700 font-semibold" : "text-stone-500"}
                        >
                          {validPick
                            ? `✓ Selected: ${city}`
                            : "Pick your city from the PostEx delivery list above."}
                        </span>
                        <span className="text-stone-400">
                          {POSTEX_SHIPPING_CITIES.length} PostEx cities
                        </span>
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Full Address */}
              <div className="sm:col-span-2 space-y-1">
                <label className="font-semibold text-stone-800">
                  Complete Street Address (House/Shop #, Street, Area) <span className="text-rose-600">*</span>
                </label>
                <textarea
                  required
                  rows={2}
                  data-testid="checkout-address-input"
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  placeholder="House # 12, Street 4, Gulberg III / DHA Phase 5..."
                  className="w-full px-3.5 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                />
              </div>

              {/* Order Notes */}
              <div className="sm:col-span-2 space-y-1">
                <label className="font-semibold text-stone-800">
                  Special Order Instructions / Tailoring Notes (Optional)
                </label>
                <input
                  type="text"
                  data-testid="checkout-notes-input"
                  value={orderNotes}
                  onChange={(e) => setOrderNotes(e.target.value)}
                  placeholder="e.g. Please send extra champagne gold sample if possible"
                  className="w-full px-3.5 py-2 bg-[#FAF8F5] border border-stone-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                />
              </div>

            </div>
          </div>

          {/* Payment Method Selector */}
          <div className="bg-white p-6 rounded-2xl border border-[#E5E0D8] shadow-sm space-y-4">
            <h2 className="font-heading font-bold text-base text-stone-900 flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-[#D4AF37]" />
              2. Select Payment Method
            </h2>

            <div className="space-y-3">
              
              {/* Option 1: Cash on Delivery */}
              <label 
                data-testid="payment-method-cod"
                className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition ${
                  paymentMethod === "cod" ? "bg-[#D4AF37]/10 border-[#D4AF37] ring-1 ring-[#D4AF37]" : "bg-[#FAF8F5] border-stone-200 hover:border-stone-300"
                }`}
              >
                <input
                  type="radio"
                  name="payment"
                  checked={paymentMethod === "cod"}
                  onChange={() => setPaymentMethod("cod")}
                  className="mt-0.5 text-[#D4AF37] focus:ring-[#D4AF37]"
                />
                <div className="text-xs space-y-0.5">
                  <div className="font-bold text-stone-900 flex items-center gap-2">
                    <span>Cash on Delivery (COD)</span>
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-semibold rounded">Recommended</span>
                  </div>
                  <p className="text-stone-500">Pay cash directly to the rider upon parcel delivery at your doorstep.</p>
                </div>
              </label>

              {/* Option 2: Bank Transfer (Meezan Bank) */}
              <label 
                data-testid="payment-method-bank"
                className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition ${
                  paymentMethod === "bank_transfer" ? "bg-[#D4AF37]/10 border-[#D4AF37] ring-1 ring-[#D4AF37]" : "bg-[#FAF8F5] border-stone-200 hover:border-stone-300"
                }`}
              >
                <input
                  type="radio"
                  name="payment"
                  checked={paymentMethod === "bank_transfer"}
                  onChange={() => setPaymentMethod("bank_transfer")}
                  className="mt-0.5 text-[#D4AF37] focus:ring-[#D4AF37]"
                />
                <div className="text-xs space-y-0.5 w-full">
                  <div className="font-bold text-stone-900 flex items-center gap-2">
                    <span>Direct Bank Transfer (Meezan Bank)</span>
                  </div>
                  <p className="text-stone-500">Instant online transfer via any Pakistani banking app (1-Link).</p>

                  {paymentMethod === "bank_transfer" && settings.bank_details && (
                    <div className="mt-3 p-3 bg-white rounded-lg border border-[#D4AF37]/40 space-y-2 text-stone-800">
                      <div className="flex justify-between items-center text-xs">
                        <span><strong>Bank:</strong> {settings.bank_details.bank_name}</span>
                      </div>
                      <div className="flex justify-between items-center text-xs">
                        <span><strong>Account Title:</strong> {settings.bank_details.account_title}</span>
                      </div>
                      <div className="flex justify-between items-center text-xs bg-[#FAF8F5] p-1.5 rounded">
                        <span><strong>Account #:</strong> {settings.bank_details.account_number}</span>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(settings.bank_details.account_number, "Account Number")}
                          className="text-[10px] text-[#996515] font-bold flex items-center gap-1 hover:underline"
                        >
                          {copiedField === "Account Number" ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                          Copy
                        </button>
                      </div>
                      <div className="flex justify-between items-center text-xs bg-[#FAF8F5] p-1.5 rounded">
                        <span className="truncate pr-2"><strong>IBAN:</strong> {settings.bank_details.iban}</span>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(settings.bank_details.iban, "IBAN")}
                          className="text-[10px] text-[#996515] font-bold flex items-center gap-1 hover:underline shrink-0"
                        >
                          {copiedField === "IBAN" ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                          Copy
                        </button>
                      </div>
                      <p className="text-[11px] text-stone-500 italic">
                        * After transfer, please WhatsApp your receipt screenshot to {settings.whatsapp_number}.
                      </p>
                    </div>
                  )}
                </div>
              </label>

              {/* Option 3: EasyPaisa */}
              <label 
                data-testid="payment-method-easypaisa"
                className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition ${
                  paymentMethod === "easypaisa" ? "bg-[#D4AF37]/10 border-[#D4AF37] ring-1 ring-[#D4AF37]" : "bg-[#FAF8F5] border-stone-200 hover:border-stone-300"
                }`}
              >
                <input
                  type="radio"
                  name="payment"
                  checked={paymentMethod === "easypaisa"}
                  onChange={() => setPaymentMethod("easypaisa")}
                  className="mt-0.5 text-[#D4AF37] focus:ring-[#D4AF37]"
                />
                <div className="text-xs space-y-0.5 w-full">
                  <div className="font-bold text-stone-900">EasyPaisa Mobile Account</div>
                  <p className="text-stone-500">Send payment directly to our verified EasyPaisa account.</p>

                  {paymentMethod === "easypaisa" && settings.easypaisa_details && (
                    <div className="mt-3 p-3 bg-white rounded-lg border border-emerald-300 space-y-2 text-stone-800">
                      <div className="flex justify-between items-center text-xs">
                        <span><strong>Account Name:</strong> {settings.easypaisa_details.account_name}</span>
                      </div>
                      <div className="flex justify-between items-center text-xs bg-emerald-50/60 p-1.5 rounded">
                        <span><strong>EasyPaisa #:</strong> {settings.easypaisa_details.account_number}</span>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(settings.easypaisa_details.account_number, "EasyPaisa Number")}
                          className="text-[10px] text-emerald-700 font-bold flex items-center gap-1 hover:underline"
                        >
                          {copiedField === "EasyPaisa Number" ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                          Copy Number
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </label>

              {/* Option 4: JazzCash */}
              <label 
                data-testid="payment-method-jazzcash"
                className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition ${
                  paymentMethod === "jazzcash" ? "bg-[#D4AF37]/10 border-[#D4AF37] ring-1 ring-[#D4AF37]" : "bg-[#FAF8F5] border-stone-200 hover:border-stone-300"
                }`}
              >
                <input
                  type="radio"
                  name="payment"
                  checked={paymentMethod === "jazzcash"}
                  onChange={() => setPaymentMethod("jazzcash")}
                  className="mt-0.5 text-[#D4AF37] focus:ring-[#D4AF37]"
                />
                <div className="text-xs space-y-0.5 w-full">
                  <div className="font-bold text-stone-900">JazzCash Mobile Account</div>
                  <p className="text-stone-500">Quick transfer via JazzCash app or *786# menu.</p>

                  {paymentMethod === "jazzcash" && settings.jazzcash_details && (
                    <div className="mt-3 p-3 bg-white rounded-lg border border-rose-300 space-y-2 text-stone-800">
                      <div className="flex justify-between items-center text-xs">
                        <span><strong>Account Name:</strong> {settings.jazzcash_details.account_name}</span>
                      </div>
                      <div className="flex justify-between items-center text-xs bg-rose-50/60 p-1.5 rounded">
                        <span><strong>JazzCash #:</strong> {settings.jazzcash_details.account_number}</span>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(settings.jazzcash_details.account_number, "JazzCash Number")}
                          className="text-[10px] text-rose-700 font-bold flex items-center gap-1 hover:underline"
                        >
                          {copiedField === "JazzCash Number" ? <Check className="w-3 h-3 text-rose-600" /> : <Copy className="w-3 h-3" />}
                          Copy Number
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </label>

            </div>
          </div>

        </div>

        {/* Right Order Review (5 cols) */}
        <div className="lg:col-span-5 space-y-4">
          <div className="bg-white p-6 rounded-2xl border border-[#E5E0D8] shadow-luxury space-y-5 sticky top-28">
            <h3 className="font-heading font-bold text-base text-stone-900 border-b border-[#E5E0D8] pb-3">
              Items in Order ({cartItems.length})
            </h3>

            {/* Mini Items List */}
            <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
              {cartItems.map((item) => (
                <div key={item.cartItemId} className="flex items-center justify-between text-xs gap-3">
                  <div className="flex items-center gap-2">
                    <img src={resolveImageUrl(item.image)} alt={item.product_name} className="w-10 h-10 rounded-lg object-cover border border-stone-200" />
                    <div>
                      <h4 className="font-semibold text-stone-900 line-clamp-1">{item.product_name}</h4>
                      <span className="text-[10px] text-stone-500">{item.quantity}x Set ({item.selected_color})</span>
                    </div>
                  </div>
                  <span className="font-bold text-stone-900 shrink-0">
                    Rs. {item.item_total.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>

            {/* Calculations */}
            <div className="space-y-2 pt-3 border-t border-[#E5E0D8] text-xs">
              <div className="flex justify-between text-stone-600">
                <span>Subtotal</span>
                <span data-testid="checkout-subtotal" className="font-bold text-stone-900">Rs. {subtotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-stone-600">
                <span>
                  Delivery Charges
                  <span className="block text-[10px] text-stone-400">
                    Weight ~{totalWeightKg.toFixed(1)} KG
                  </span>
                </span>
                <span data-testid="checkout-delivery-fee" className="font-bold text-stone-900">Rs. {shippingFee.toLocaleString()}</span>
              </div>
              {codCharge > 0 && (
                <div className="flex justify-between text-stone-600">
                  <span>COD Charges (4%)</span>
                  <span data-testid="checkout-cod-charge" className="font-bold text-stone-900">Rs. {codCharge.toLocaleString()}</span>
                </div>
              )}
              <div className="pt-2 border-t border-[#E5E0D8] flex justify-between items-baseline">
                <span className="font-heading font-bold text-base text-stone-900">Grand Total</span>
                <div className="text-right">
                  <span 
                    data-testid="checkout-grand-total"
                    className="font-heading text-2xl font-extrabold text-stone-900"
                  >
                    Rs. {grandTotal.toLocaleString()}
                  </span>
                  <span className="text-[10px] text-stone-500 block">PKR</span>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              data-testid="checkout-place-order-btn"
              className="w-full py-3.5 px-4 bg-[#1C1917] hover:bg-[#D4AF37] text-white hover:text-[#1C1917] text-xs font-bold rounded-xl shadow-lg transition flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {submitting ? (
                <span>Placing Order...</span>
              ) : (
                <>
                  <ShieldCheck className="w-4 h-4" />
                  <span>Confirm & Place Order (Rs. {grandTotal.toLocaleString()})</span>
                </>
              )}
            </button>

            <p className="text-[10px] text-stone-400 text-center leading-tight">
              By confirming, your order will be registered in our system and an SMS/WhatsApp confirmation will be sent.
            </p>

          </div>
        </div>

      </form>
    </div>
  );
}
